import React from "react";
import { Box, Stack } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import { useRequestListData } from "./hooks/useRequestListData";
import RequestListTabs from "./partials/RequestListTabs";
import CompletedRequestsGrid from "./partials/CompletedRequestsGrid";
import RequestDetailsGrid from "./partials/RequestDetailsGrid";
import OutOfRequestServicesGrid from "./partials/OutOfRequestServicesGrid";
import PendingRequestsTab from "./partials/PendingRequestsTab";
import AllRequestsTab from "./partials/AllRequestsTab";
import AllArrivalsRequestsTab from "./partials/AllArrivalsRequestsTab";

export interface RequestListProps {
  open: boolean;
  onClose: () => void;
  ptId?: string | number;
  upn?: string;
  title?: string;
  description?: string;
}

export default function RequestList({
  open,
  onClose,
  ptId,
  upn,
  title = "",
  description = "",
}: RequestListProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);
  const {
    activeTab,
    setActiveTab,
    completedRequests,
    requestDetails,
    pendingRequestDetails,
    outOfRequestServices,
    allRequests,
    allVisitsRequests,
    selectedRequestId,
    setSelectedRequestId,
    loading,
    fetchAllVisitsRequests,
    hasCancelPrivilege,
    refetch,
    fetchRequestDetails,
    detailsLoading,
  } = useRequestListData({ ptId, upn, open });

  // Handle date range change for All Arrivals tab
  const handleDateRangeChange = (days: string) => {
    const daysNum = days === "today" ? 1 : Number(days);
    fetchAllVisitsRequests(daysNum);
  };

  const handleActionComplete = React.useCallback(() => {
    refetch();
    fetchAllVisitsRequests(1);
    if (selectedRequestId) {
      fetchRequestDetails(Number(selectedRequestId));
    }
  }, [refetch, fetchAllVisitsRequests, selectedRequestId, fetchRequestDetails]);



  const pageTitle =
    title || t("request-list.title", "default", { defaultValue: "Yapılan İstekler" });

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader={false}
      title={pageTitle}
      showPatientData={true}
      rowID={ptId}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 5,
          minHeight: 720,
        }}
      >
        <RequestListTabs value={activeTab} onChange={setActiveTab} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
        {activeTab === "completed" && (
          <Stack spacing={1} sx={{ alignSelf: "flex-start", width: "100%" }}>
            <CompletedRequestsGrid
              rows={completedRequests}
              loading={loading}
              onRowSelect={(row) => setSelectedRequestId(row.id)}
              selectedRequestId={selectedRequestId}
              hasCancelPrivilege={hasCancelPrivilege}
              currentUser={user?.username || ""}
              ptId={ptId}
              onCancel={handleActionComplete}
              onCancelApproved={handleActionComplete}
            />
            <RequestDetailsGrid
              rows={requestDetails}
              loading={loading || detailsLoading}
              onRefresh={handleActionComplete}
              title={t("request-list.request-details", "default", {
                defaultValue: "İstek Detayları",
              })}
              currentUser={user?.username || ""}
              ptId={ptId}
            />
            <OutOfRequestServicesGrid
              rows={outOfRequestServices}
              loading={loading}
            />
          </Stack>
        )}

        {activeTab === "pending" && (
          <PendingRequestsTab
            requestDetails={pendingRequestDetails}
            outOfRequestServices={outOfRequestServices}
            loading={loading || detailsLoading}
            currentUser={user?.username || ""}
            ptId={ptId}
            onRefresh={handleActionComplete}
          />
        )}

        {activeTab === "all" && (
          <AllRequestsTab
            rows={allRequests}
            loading={loading}
          />
        )}

        {activeTab === "all-arrivals" && (
          <AllArrivalsRequestsTab
            allVisitsRequests={allVisitsRequests}
            requestDetails={requestDetails}
            outOfRequestServices={outOfRequestServices}
            loading={loading || detailsLoading}
            onDateRangeChange={handleDateRangeChange}
            onRowSelect={(row) => setSelectedRequestId(row.requestId ?? row.id)}
            onDetailsRefresh={handleActionComplete}
            currentUser={user?.username || ""}
            ptId={ptId}
          />
        )}
        </Box>
      </Box>
    </OpenComponentDialog>
  );
}
